import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { PriceDistribution } from './PriceDistribution'
import { Count } from '../../_kernel/Count'

export const ListOfPriceDistributionValidation = z.object({
  list: z.instanceof(PriceDistribution).array(),
  selectedRange: z.instanceof(Count).array().optional(),
})

type mapCb<T> = (priceDistribution: PriceDistribution, index: number) => T
export class ListOfPriceDistribution extends Model {
  static create({ list, selectedRange }: z.infer<typeof ListOfPriceDistributionValidation>) {
    ListOfPriceDistributionValidation.parse({ list, selectedRange })

    return new ListOfPriceDistribution(list, selectedRange)
  }

  static fromJSON({ list, selectedRange }: ReturnType<ListOfPriceDistribution['toJSON']>) {
    const model = new ListOfPriceDistribution(
      list.map(({ value, count }) => PriceDistribution.create({ value, count })),
      selectedRange?.map(({ value }) => Count.create({ value })) ?? []
    )

    return model
  }

  static fromLegacyContext(list: { min: number; count: number }[] = []) {
    const listNormalized = [...list]

    if (list.length === 1) {
      const min = list.at(0)?.min ?? 20
      listNormalized.unshift({ min: min - 20, count: 0 })
    }

    return new ListOfPriceDistribution(
      listNormalized.map(({ min, count }) => PriceDistribution.create({ value: min, count })) ?? []
    )
  }

  constructor(
    private readonly _list: PriceDistribution[],
    private readonly _selectedRange?: Count[]
  ) {
    super()
  }

  get serialized() {
    return JSON.stringify(this._list.map(priceDistribution => priceDistribution.getValue()))
  }

  getRange() {
    return (this._list.at(1)?.getValue() ?? 0) - (this._list.at(0)?.getValue() ?? 0)
  }

  getMax() {
    return this._list.at(-1)
  }

  setSelectedRange(range: number[]) {
    return ListOfPriceDistribution.fromJSON({
      ...this.toJSON(),
      selectedRange: range.map(value => Count.create({ value })),
    })
  }

  getMin() {
    return this._list.at(0)
  }

  getMinValue() {
    return this._list.at(0)?.getValue() ?? 0
  }

  getMaxValue() {
    return this._list.at(-1)?.getValue() ?? 0
  }

  isEmpty() {
    return this._list.length === 0
  }

  getList() {
    return this._list
  }

  getClosestByValue(value: number, { get = 'floor' }: { get?: 'floor' | 'ceil' } = {}) {
    const filteredList = this._list.filter(item => {
      const itemValue = item.getValue()
      if (get === 'floor') {
        return itemValue <= value
      }

      return itemValue >= value
    })

    if (filteredList.length === 0) {
      return null
    }

    return filteredList.reduce((prev, curr) => {
      const prevValue = prev.getValue()
      const currValue = curr.getValue()

      if (Math.abs(currValue - value) <= Math.abs(prevValue - value)) {
        return curr
      }

      return prev
    })
  }

  findIndexByValue(value: number) {
    return this._list.findIndex(priceDistribution => priceDistribution.getValue() === value)
  }

  getScale() {
    const max = this.getMax()?.getCount() ?? 0
    const min = this.getMin()?.getCount() ?? 0

    let scale: 'linear' | 'log' | 'sqrt' = 'sqrt'
    const ratio = max / (min || 1)

    if (max === min) {
      scale = 'linear'
    }

    if (ratio >= 3) {
      scale = 'sqrt'
    }

    if (ratio >= 6) {
      scale = 'log'
    }

    return scale
  }

  getData() {
    return this.map(info => info.getCount())
  }

  map<T>(callback: mapCb<T>) {
    return this.getList().map(callback)
  }

  mapOnRange<T>(callbackOutsideRange: mapCb<T>, callbackOnSelectedRange: mapCb<T>) {
    const minRange = this._selectedRange?.[0]?.value ?? this.getMin()?.getValue() ?? 0
    const maxRange = this._selectedRange?.[1]?.value ?? (this.getMax()?.getValue() ?? 0) + 1

    return this.getList().map((priceDistribution, index) => {
      if (priceDistribution.getValue() <= minRange || priceDistribution.getValue() > maxRange) {
        return callbackOutsideRange(priceDistribution, index)
      }
      return callbackOnSelectedRange(priceDistribution, index)
    })
  }

  toJSON() {
    return {
      list: this._list.map(priceDistribution => priceDistribution.toJSON()),
      selectedRange: this._selectedRange?.map(count => count.toJSON()),
    }
  }
}
